import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Grid,
  useTheme,
  useMediaQuery,
  Divider,
} from '@mui/material';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CloudIcon from '@mui/icons-material/Cloud';
import BusinessIcon from '@mui/icons-material/Business';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
import TimelineIcon from '@mui/icons-material/Timeline';
import SecurityIcon from '@mui/icons-material/Security';
import PeopleIcon from '@mui/icons-material/People';
import SpeedIcon from '@mui/icons-material/Speed';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import BuildIcon from '@mui/icons-material/Build';
import MemoryIcon from '@mui/icons-material/Memory';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SchoolIcon from '@mui/icons-material/School';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import WebIcon from '@mui/icons-material/Web';
import MobileFriendlyIcon from '@mui/icons-material/MobileFriendly';
import StorageIcon from '@mui/icons-material/Storage';
import PsychologyIcon from '@mui/icons-material/Psychology';
import DesignServicesIcon from '@mui/icons-material/DesignServices';

const DigitalTransformation: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Digital transformation pillars
  const transformationPillars = [
    {
      title: "Strategy & Roadmap",
      description: "Comprehensive digital strategy aligned with business objectives and a clear transformation roadmap",
      icon: <EmojiObjectsIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
    },
    {
      title: "Process Optimization",
      description: "Streamlined business processes redesigned for the digital age and maximum efficiency",
      icon: <TimelineIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
    },
    {
      title: "Technology Implementation",
      description: "Integration of cutting-edge technologies tailored to your organizational needs",
      icon: <BuildIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
    },
    {
      title: "People & Culture",
      description: "Change management and culture transformation to embrace digital ways of working",
      icon: <PeopleIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
    },
    {
      title: "Data Intelligence",
      description: "Data-driven decision making capabilities and advanced analytics implementation",
      icon: <DataUsageIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
    },
    {
      title: "Customer Experience",
      description: "Exceptional digital experiences that meet modern customer expectations",
      icon: <TouchAppIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
    },
  ];

  // Key technologies
  const keyTechnologies = [
    {
      title: "Cloud Computing",
      description: "Scalable cloud infrastructure and migration strategies for enhanced flexibility and efficiency",
      icon: <CloudIcon fontSize="large" />,
    },
    {
      title: "AI & Machine Learning",
      description: "Intelligent solutions that automate processes and deliver actionable insights",
      icon: <PsychologyIcon fontSize="large" />,
    },
    {
      title: "Internet of Things",
      description: "Connected devices and systems that enable smart operations and data collection",
      icon: <MemoryIcon fontSize="large" />,
    },
    {
      title: "Data Analytics",
      description: "Advanced analytics capabilities that transform raw data into strategic business value",
      icon: <AssessmentIcon fontSize="large" />,
    },
    {
      title: "API Integration",
      description: "Seamless connectivity between systems and applications for unified operations",
      icon: <IntegrationInstructionsIcon fontSize="large" />,
    },
    {
      title: "Digital Experience Platforms",
      description: "Omnichannel solutions for creating exceptional customer and employee experiences",
      icon: <DesignServicesIcon fontSize="large" />,
    },
  ];

  // Transformation process
  const transformationProcess = [
    {
      step: "Assessment",
      description: "Comprehensive analysis of your current digital maturity, capabilities, and opportunities",
    },
    {
      step: "Strategy",
      description: "Development of a tailored digital transformation roadmap aligned with business goals",
    },
    {
      step: "Design",
      description: "Architectural planning and solution design for your target digital state",
    },
    {
      step: "Implementation",
      description: "Execution of transformation initiatives using agile methodologies",
    },
    {
      step: "Change Management",
      description: "Preparing your organization and people for digital changes and new ways of working",
    },
    {
      step: "Continuous Improvement",
      description: "Ongoing optimization and refinement of digital capabilities for sustainable success",
    },
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
          color: 'white',
          py: 12,
          position: 'relative',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom textAlign="center">
              Digital Transformation Services
            </Typography>
            <Typography variant="h6" paragraph align="center" sx={{ maxWidth: '800px', mx: 'auto', opacity: 0.9 }}>
              Reimagine your business for the digital age
            </Typography>
          </motion.div>
        </Container>
        
        {/* Decorative Elements */}
        <Box sx={{
          position: 'absolute',
          top: 60,
          right: 120,
          width: '150px',
          height: '150px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: 40,
          left: 100,
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
      </Box>

      {/* Overview Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Typography 
                variant="h4" 
                component="h2" 
                gutterBottom 
                fontWeight="bold"
                color="#0c2340"
              >
                Navigate Your Digital Journey
              </Typography>
              <Typography paragraph>
                In today's rapidly evolving business landscape, digital transformation is no longer optional—it's an
                imperative for survival and growth. Right Track Technologies Limited partners with organizations to
                navigate this complex journey, delivering comprehensive digital transformation solutions that enhance
                operational efficiency, customer experience, and competitive advantage.
              </Typography>
              <Typography paragraph>
                Our comprehensive approach addresses all aspects of transformation—from strategy and process 
                redesign to technology implementation and cultural change. We combine deep industry knowledge 
                with technical expertise to deliver transformative solutions that drive measurable business outcomes.
              </Typography>
              <Typography paragraph>
                Whether you're looking to modernize legacy systems, implement new digital capabilities, or 
                completely reimagine your business model, our digital transformation consultants provide the 
                guidance and expertise needed to succeed in the digital age.
              </Typography>
            </motion.div>
          </Box>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Box 
                component="img"
                src="https://images.unsplash.com/photo-1535551951406-a19828b0a76b?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80"
                alt="Digital Transformation"
                sx={{
                  width: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                }}
              />
            </motion.div>
          </Box>
        </Box>
      </Container>

      {/* Transformation Pillars Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              fontWeight="bold"
              color="#0c2340"
              textAlign="center"
            >
              The Pillars of Digital Transformation
            </Typography>
            <Typography 
              textAlign="center" 
              sx={{ 
                maxWidth: 800, 
                mx: 'auto', 
                mb: 6 
              }}
            >
              Our holistic approach ensures all aspects of transformation are addressed
            </Typography>
          </motion.div>

          <Box sx={{ 
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
            justifyContent: 'center',
          }}>
            {transformationPillars.map((pillar, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                style={{ width: isMobile ? '100%' : 'calc(33.333% - 16px)' }}
              >
                <Paper
                  elevation={2}
                  sx={{
                    p: 4,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  <Box sx={{ mb: 2 }}>
                    {pillar.icon}
                  </Box>
                  <Typography variant="h5" component="h3" gutterBottom fontWeight="bold">
                    {pillar.title}
                  </Typography>
                  <Typography color="text.secondary">
                    {pillar.description}
                  </Typography>
                </Paper>
              </motion.div>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Technology Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom 
            fontWeight="bold"
            color="#0c2340"
            textAlign="center"
          >
            Key Technologies for Digital Transformation
          </Typography>
          <Typography 
            textAlign="center" 
            sx={{ 
              maxWidth: 800, 
              mx: 'auto', 
              mb: 6 
            }}
          >
            Leveraging cutting-edge technologies to power your digital future
          </Typography>
        </motion.div>
      
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 4,
          justifyContent: 'center'
        }}>
          {keyTechnologies.map((tech, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              style={{ width: isMobile ? '100%' : 'calc(33.333% - 16px)' }}
            >
              <Paper
                elevation={2}
                sx={{
                  p: 4,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
                  },
                }}
              >
                <Box sx={{ color: theme.palette.primary.main, mb: 2 }}>
                  {tech.icon}
                </Box>
                <Typography variant="h5" component="h3" gutterBottom fontWeight="bold">
                  {tech.title}
                </Typography>
                <Typography color="text.secondary">
                  {tech.description}
                </Typography>
              </Paper>
            </motion.div>
          ))}
        </Box>
      </Container>

      {/* Transformation Process Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              fontWeight="bold"
              color="#0c2340"
              textAlign="center"
            >
              Our Digital Transformation Process
            </Typography>
            <Typography 
              textAlign="center" 
              sx={{ 
                maxWidth: 800, 
                mx: 'auto', 
                mb: 6 
              }}
            >
              A structured approach that delivers sustainable digital transformation
            </Typography>
          </motion.div>

          <Box sx={{ 
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            flexWrap: 'wrap',
            gap: 4,
            justifyContent: 'center',
          }}>
            {transformationProcess.map((process, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                style={{ width: isMobile ? '100%' : 'calc(33.333% - 16px)' }}
              >
                <Paper
                  elevation={2}
                  sx={{
                    p: 4,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    position: 'relative',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  <Typography 
                    variant="h2" 
                    component="div" 
                    sx={{ 
                      position: 'absolute', 
                      top: -15, 
                      right: 10, 
                      opacity: 0.1,
                      fontWeight: 'bold',
                      color: theme.palette.primary.main,
                      fontSize: '5rem',
                      zIndex: 0
                    }}
                  >
                    {index + 1}
                  </Typography>
                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Typography variant="h5" component="h3" gutterBottom fontWeight="bold">
                      {process.step}
                    </Typography>
                    <Typography color="text.secondary">
                      {process.description}
                    </Typography>
                  </Box>
                </Paper>
              </motion.div>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom 
            fontWeight="bold"
            color="#0c2340"
            textAlign="center"
          >
            Benefits of Digital Transformation
          </Typography>
          <Typography 
            textAlign="center" 
            sx={{ 
              maxWidth: 800, 
              mx: 'auto', 
              mb: 6 
            }}
          >
            Unlock new opportunities and competitive advantages
          </Typography>
        </motion.div>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <List>
              {[
                {
                  benefit: "Enhanced Customer Experience",
                  description: "Create seamless, personalized experiences across all touchpoints",
                  icon: <TouchAppIcon />
                },
                {
                  benefit: "Operational Excellence",
                  description: "Streamline processes and increase efficiency through automation",
                  icon: <SpeedIcon />
                },
                {
                  benefit: "Business Agility",
                  description: "Quickly adapt to market changes and emerging opportunities",
                  icon: <AutorenewIcon />
                },
                {
                  benefit: "Data-Driven Decision Making",
                  description: "Leverage analytics for more informed strategic decisions",
                  icon: <AssessmentIcon />
                },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <ListItem sx={{ py: 1.5 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.benefit} 
                      secondary={item.description}
                      primaryTypographyProps={{ fontWeight: 'bold' }}
                    />
                  </ListItem>
                </motion.div>
              ))}
            </List>
          </Box>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <List>
              {[
                {
                  benefit: "New Revenue Streams",
                  description: "Unlock innovative business models and digital products",
                  icon: <TrendingUpIcon />
                },
                {
                  benefit: "Cost Optimization",
                  description: "Reduce operational costs through digital efficiencies",
                  icon: <BusinessIcon />
                },
                {
                  benefit: "Enhanced Security",
                  description: "Implement modern security measures for digital assets",
                  icon: <SecurityIcon />
                },
                {
                  benefit: "Improved Collaboration",
                  description: "Enable seamless teamwork across locations and departments",
                  icon: <PeopleIcon />
                },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <ListItem sx={{ py: 1.5 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.benefit} 
                      secondary={item.description}
                      primaryTypographyProps={{ fontWeight: 'bold' }}
                    />
                  </ListItem>
                </motion.div>
              ))}
            </List>
          </Box>
        </Box>
      </Container>

      {/* Industry Solutions Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              fontWeight="bold"
              color="#0c2340"
              textAlign="center"
            >
              Industry-Specific Digital Solutions
            </Typography>
            <Typography 
              textAlign="center" 
              sx={{ 
                maxWidth: 800, 
                mx: 'auto', 
                mb: 6 
              }}
            >
              Tailored digital transformation strategies for your industry
            </Typography>
          </motion.div>

          <Box sx={{ 
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
            justifyContent: 'center',
          }}>
            {[
              {
                title: "Healthcare",
                description: "Digital healthcare solutions that improve patient outcomes, enhance operational efficiency, and ensure compliance",
                icon: <WebIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
              },
              {
                title: "Financial Services",
                description: "Secure, innovative digital banking and fintech solutions that meet evolving customer expectations",
                icon: <StorageIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
              },
              {
                title: "Manufacturing",
                description: "Smart factory solutions and Industry 4.0 implementations that optimize production and supply chains",
                icon: <AccountTreeIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
              },
              {
                title: "Retail & E-commerce",
                description: "Omnichannel experiences and digital commerce platforms that drive customer engagement and sales",
                icon: <MobileFriendlyIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
              },
            ].map((industry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                style={{ width: isMobile ? '100%' : 'calc(50% - 16px)' }}
              >
                <Paper
                  elevation={2}
                  sx={{
                    p: 4,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  <Box sx={{ mb: 2 }}>
                    {industry.icon}
                  </Box>
                  <Typography variant="h5" component="h3" gutterBottom fontWeight="bold">
                    {industry.title}
                  </Typography>
                  <Typography color="text.secondary">
                    {industry.description}
                  </Typography>
                </Paper>
              </motion.div>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Call to Action Section */}
      <Box sx={{ bgcolor: '#0c2340', color: 'white', py: 10 }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            style={{ textAlign: 'center' }}
          >
            <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
              Begin Your Digital Transformation Journey
            </Typography>
            <Typography sx={{ mb: 4, opacity: 0.9 }}>
              Contact us today to discuss how our digital transformation services can help your 
              organization thrive in the digital economy.
            </Typography>
            <Button
              component={Link}
              to="/contact"
              variant="contained"
              color="primary"
              size="large"
              sx={{
                bgcolor: 'white',
                color: '#0c2340',
                px: 4,
                py: 1.5,
                fontWeight: 'bold',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.9)',
                },
              }}
            >
              Start Your Transformation
            </Button>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default DigitalTransformation; 